import React, { useState, useEffect, useCallback, useMemo } from "react";
import { SelectableDataGrid } from "../../../common/SelectableDataGrid";
import MenuItem from "@mui/material/MenuItem";
import HttpClient, { Methods } from "../../../../services/HttpClient";
import { endpoints } from "../../../../constants/Endpoints";
import moment from "moment"; // retained for potential other uses
import { formatDisplayDate } from "../../../../util/dateFormat";
import {
  Grid,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  GlobalStyles,
} from "@mui/material";
import { OverviewHeaderBar } from "../../../common/OverviewHeaderBar";
import { useNavigate } from "react-router-dom";
import { validateEmail } from "../../../../services/ValidationService";
import { appRoutes } from "../../../../constants/AppRoutes";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/nl";
import { messageConstants } from "../../../../constants/MessageConstants";
import TranslationService from "../../../../services/TranslationService";

// Icons
import AddBoxIcon from "@mui/icons-material/AddBox";
import RefreshIcon from "@mui/icons-material/Refresh";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import RedoIcon from "@mui/icons-material/Redo";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import ArchiveIcon from "@mui/icons-material/Archive";

// Removed LocalizationProvider / MobileDatePicker (x-date-pickers) to avoid peer dependency
const StyledLocalizationProvider = React.Fragment;

// Dialog for adding a new renter
function AddRenterDialog({ open, onClose, onSubmit, isSubmitting }) {
  const [form, setForm] = useState({
    applicationUserFirstName: "",
    applicationUserLastName: "",
    applicationUserEmail: "",
    applicationUserPhoneNumber: "",
    applicationUserLocale: "nl_NL",
    paymentExpiresAt: null,
  });

  useEffect(() => {
    if (!open) {
      setForm({
        applicationUserFirstName: "",
        applicationUserLastName: "",
        applicationUserEmail: "",
        applicationUserPhoneNumber: "",
        applicationUserLocale: "nl_NL",
        paymentExpiresAt: null,
      });
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleDateChange = (value) => {
    setForm((f) => ({ ...f, paymentExpiresAt: value ? value.toDate() : null }));
  };

  const submit = () => onSubmit(form);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nieuwe inschrijving</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              required
              name="applicationUserFirstName"
              label="Voornaam"
              fullWidth
              value={form.applicationUserFirstName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              name="applicationUserLastName"
              label="Achternaam"
              fullWidth
              value={form.applicationUserLastName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              required
              name="applicationUserEmail"
              label="E-mail"
              type="email"
              fullWidth
              value={form.applicationUserEmail}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              name="applicationUserPhoneNumber"
              label="Telefoon"
              fullWidth
              value={form.applicationUserPhoneNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              name="applicationUserLocale"
              label="Taal"
              fullWidth
              value={form.applicationUserLocale}
              onChange={handleChange}
            >
              <MenuItem value="nl_NL">NL</MenuItem>
              <MenuItem value="en_US">EN</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale="nl"
            >
              <DatePicker
                label={TranslationService?.l?.betaaldTot || "Betaald tot"}
                value={
                  form.paymentExpiresAt ? moment(form.paymentExpiresAt) : null
                }
                format="DD/MM/YYYY"
                onChange={(m) => {
                  setForm((f) => ({
                    ...f,
                    paymentExpiresAt: m && m.isValid() ? m.toDate() : null,
                  }));
                }}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      "& .MuiInputBase-root": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiFormLabel-root": {
                        backgroundColor: "transparent",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Annuleren
        </Button>
        <Button onClick={submit} variant="contained" disabled={isSubmitting}>
          Opslaan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RenterTable(props) {
  const refreshData = () => {
    setLoading(true);
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });
  };

  const styles = {
    paymentExpiresAt: {
      padding: "5px 10px",
      minWidth: "100px",
      textWrap: "nowrap",
    },
    expired: {
      backgroundColor: "#ec2027",
      color: "white",
      borderRadius: "50px",
    },
    exportToCsvButton: {
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": {
        color: "#d01717",
      },
    },
    exportToCsvLink: {
      color: "white",
      textDecoration: "none",
    },
    newButton: {
      backgroundColor: "#eb008b",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    refreshButton: {
      backgroundColor: "#f68121",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    mailButton: {
      marginLeft: "20px",
      backgroundColor: "#ec2027",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "yellow !important",
      },
    },
    archiveExpiredButton: {
      backgroundColor: "#9e9e9e",
      color: "#ffffff",
      "&:hover": {
        color: "#ec2027",
      },
    },
  };

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.renter.base,
      null
    );
    if (response.ok) {
      return await response.json();
    }

    // Logout if this call fails.
    window.location.href = "/Identity/Account/Logout";
  }, []);

  const sendData = async (newRenter) => {
    setLoading(true);

    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.renter.create,
      newRenter
    );

    const message = await response.json();

    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }

    refreshData();
  };

  const sendAccountActivationEmail = async (renterId, email) => {
    setLoading(true);

    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.renter.sendAccountActivationEmail +
        "?renterId=" +
        renterId +
        "&email=" +
        email
    );

    const message = await response.json();

    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    setLoading(false);
  };

  const sendBulkActivationEmails = async (selection) => {
    const ids = Array.from(selection.ids);

    if (!ids?.length) return;

    const count = ids.length;

    const confirm = await Swal.fire({
      title: "Activatie e-mail versturen",
      text: `Wil je een activatiemail versturen naar ${count} inschrijving(en)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#eb008b",
      confirmButtonText: "Versturen",
      cancelButtonText: "Annuleren",
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);

    const emailById = new Map(
      data
        .filter((r) => r && ids.includes(r.id))
        .map((r) => [r.id, r.applicationUserEmail])
    );

    const results = await Promise.allSettled(
      ids.map((id) =>
        HttpClient.doRequest(
          Methods.POST,
          `${
            endpoints.renter.sendAccountActivationEmail
          }?renterId=${id}&email=${encodeURIComponent(
            emailById.get(id) || ""
          )}`,
          null
        ).then((r) => r.json().catch(() => null))
      )
    );

    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;

    const Toast = Swal.mixin({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

    Toast.fire({
      icon: failed ? "warning" : "success",
      title: failed
        ? `Mails verzonden: ${succeeded}, mislukt: ${failed}`
        : `Mails verzonden naar ${succeeded} inschrijving(en)`,
    });

    setLoading(false);
  };

  const archiveRenter = async (id) => {
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.renter.base + id,
      null
    );
    return response?.ok;
  };

  const archiveSelected = async (selection) => {
    const ids = Array.from(selection.ids);

    if (!ids?.length) return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Inschrijvingen archiveren",
      text: `Weet je zeker dat je ${ids.length} inschrijving(en) wilt archiveren?`,
      showCancelButton: true,
      confirmButtonColor: "#ec2027",
      confirmButtonText: "Archiveer",
      cancelButtonText: "Annuleren",
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);

    try {
      const response = await HttpClient.doRequest(
        Methods.POST,
        endpoints.renter.bulkArchiveSelectedRenters,
        { renterIds: ids }
      );
      const message = await response.json().catch(() => null);
      if (response.ok) {
        const Toast = Swal.mixin({
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: message?.icon || "success",
          title: message?.title || "Inschrijvingen gearchiveerd",
        });
      } else {
        Swal.fire({
          icon: message?.icon || "error",
          title: message?.title || "Archiveren mislukt",
          text: message?.content || "Er is iets misgegaan.",
          confirmButtonColor: "#888",
        });
      }
    } finally {
      refreshData();
    }
  };

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  // Track current selection count from DataGrid to toggle header actions visibility
  const [selectionCount, setSelectionCount] = useState(0);
  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Acties",
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          // Keep cell layout stable to avoid DataGrid virtual scroll jitter.
          const hidden = selectionCount > 0;
          return (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                height: "100%",
                visibility: hidden ? "hidden" : "visible", // preserve width/height
              }}
            >
              {!hidden && (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    title="Activatie mail"
                    onClick={(e) => {
                      e.stopPropagation();
                      Swal.fire({
                        title: "",
                        text: "Activatiemail versturen naar?",
                        icon: "info",
                        showCancelButton: true,
                        confirmButtonColor: "#eb008b",
                        confirmButtonText: "Versturen",
                        input: "text",
                        inputPlaceholder: "E-mailadres",
                        inputValue: row.applicationUserEmail,
                        inputValidator: (email) => {
                          if (!email) return "E-mailadres is verplicht";
                          return "";
                        },
                      }).then((res) => {
                        if (res.isConfirmed)
                          sendAccountActivationEmail(row.id, res.value);
                      });
                    }}
                  >
                    <MailOutlineIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="warning"
                    title="Verleng inschrijving"
                    onClick={(e) => {
                      e.stopPropagation();
                      startSubscriptionRenewal(row.id);
                    }}
                  >
                    <RedoIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    title="Archiveer inschrijving"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchive(row);
                    }}
                  >
                    <ArchiveIcon fontSize="inherit" />
                  </IconButton>
                </>
              )}
            </Box>
          );
        },
      },
      {
        field: "applicationUserFirstName",
        headerName: "Voornaam",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "applicationUserLastName",
        headerName: "Achternaam",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "applicationUserEmail",
        headerName: "E-mail",
        flex: 1.4,
        minWidth: 200,
      },
      {
        field: "applicationUserPhoneNumber",
        headerName: "Telefoon",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "applicationUserLocale",
        headerName: "Taal",
        width: 90,
        valueGetter: (code) => {
          if (!code) return "";
          return code === "nl_NL" ? "NL" : "EN";
        },
      },
      {
        field: "fileDocumentsCount",
        headerName: "Documenten",
        width: 110,
      },
      {
        field: "paymentExpiresAt",
        headerName: "Betaald tot",
        width: 140,
        renderCell: (params) => {
          const dateVal = params?.row?.paymentExpiresAt;
          if (!dateVal) return "";
          const expired = new Date(dateVal) < new Date();
          return (
            <span
              style={
                expired
                  ? { ...styles.paymentExpiresAt, ...styles.expired }
                  : styles.paymentExpiresAt
              }
            >
              {formatDisplayDate(dateVal)}
            </span>
          );
        },
      },
    ],
    [styles, selectionCount]
  );

  const csvHeaders = [
    { label: "Achternaam", key: "applicationUserLastName" },
    { label: "Voornaam", key: "applicationUserFirstName" },
    { label: "Bedrijfsnaam", key: "applicationUserCompanyName" },
    { label: "Telefoonnummer", key: "applicationUserPhoneNumber" },
    { label: "Email", key: "applicationUserEmail" },
    { label: "Stad", key: "city" },
    { label: "Straat", key: "street" },
    { label: "Huisnummer", key: "houseNumber" },
    { label: "Postcode", key: "zipCode" },
    { label: "Toevoeging", key: "addition" },
    { label: "Netto inkomen per maand", key: "netIncomePerMonth" },
    { label: "Maximum prijs", key: "preferredMaxPrice" },
    { label: "Voorkeur stad", key: "preferredCity" },
    { label: "Voorkeur interieur", key: "preferredInterior" },
    { label: "Voorkeur wijk", key: "preferredDistrict" },
    {
      label: "Minimum aantal slaapkamers",
      key: "preferredMinNumberOfBedrooms",
    },
    { label: "Wil huren vanaf", key: "preferredStartOfRentingDate" },
    { label: "Overige wensen", key: "wishes" },
    { label: "Taalcode", key: "applicationUserLocale" },
  ];

  const [isMounted, setMounted] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });

    return () => {
      setMounted(false);
    };
  }, [isMounted, getData]);

  const [currentId, setCurrentId] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewDate, setRenewDate] = useState(moment()); // default today
  const startSubscriptionRenewal = (id) => {
    setCurrentId(id);
    setRenewDate(moment());
    setRenewOpen(true);
  };
  const closeRenewDialog = () => setRenewOpen(false);
  const submitRenewal = async () => {
    if (!renewDate || !renewDate.isValid()) {
      setRenewOpen(false);
      return;
    }
    setLoading(true);
    const dateIso = renewDate.clone().startOf("day").toISOString();
    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.renter.renewSubscription,
      { renterId: currentId, newDate: dateIso }
    );
    const message = await response.json();
    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      Toast.fire({ icon: message.icon, title: message.title });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    const newData = await getData();
    setData(newData);
    setLoading(false);
    setRenewOpen(false);
  };

  // New DataGrid specific state & helpers (moved outside of Toast mixin)
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filteredRows = useMemo(() => {
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(
      (r) =>
        r.applicationUserFirstName?.toLowerCase().includes(s) ||
        r.applicationUserLastName?.toLowerCase().includes(s) ||
        r.applicationUserEmail?.toLowerCase().includes(s) ||
        r.applicationUserPhoneNumber?.toLowerCase().includes(s)
    );
  }, [data, search]);

  // Stabilize row array reference to prevent unintended selection reset in SelectableDataGrid.
  const tableRows = useMemo(
    () =>
      (Array.isArray(filteredRows) ? filteredRows : []).filter(
        (r) => r && typeof r === "object"
      ),
    [filteredRows]
  );

  const handleAdd = async (form) => {
    if (
      !form.applicationUserFirstName ||
      !form.applicationUserLastName ||
      !form.applicationUserEmail ||
      !form.paymentExpiresAt
    ) {
      Swal.fire({
        icon: messageConstants.icons.error,
        title: "Niet alle verplichte velden zijn ingevuld",
        text: "Voornaam, Achternaam, E-mail en Betaald tot zijn verplicht.",
      });
      return;
    }
    if (!validateEmail(form.applicationUserEmail)) {
      Swal.fire({
        icon: messageConstants.icons.error,
        title: "Ongeldig e-mailadres",
        text: "Vul een geldig e-mailadres in.",
      });
      return;
    }
    const payload = {
      ...form,
      paymentExpiresAt: form.paymentExpiresAt.toISOString(),
    };
    await sendData(payload);
    setAddOpen(false);
  };

  const handleArchive = (row) => {
    Swal.fire({
      icon: "question",
      title: "Inschrijving archiveren",
      text: "Weet je zeker dat je deze inschrijving wilt archiveren?",
      showCancelButton: true,
      confirmButtonColor: "#ec2027",
      confirmButtonText: "Archiveer",
      cancelButtonText: "Annuleren",
    }).then((res) => {
      if (res.isConfirmed) {
        archiveRenter(row.id).then((ok) => {
          if (ok) {
            const Toast = Swal.mixin({
              toast: true,
              position: "bottom-end",
              showConfirmButton: false,
              timer: 2500,
              timerProgressBar: true,
            });
            Toast.fire({ icon: "success", title: "Inschrijving gearchiveerd" });
            refreshData();
          } else {
            Swal.fire({
              icon: "error",
              title: "Archiveren mislukt",
              text: "De inschrijving kon niet worden gearchiveerd.",
              confirmButtonColor: "#888",
            });
          }
        });
      }
    });
  };

  const archiveExpiredRenters = () => {
    const expired = (Array.isArray(data) ? data : []).filter(
      (r) => r?.paymentExpiresAt && new Date(r.paymentExpiresAt) < new Date()
    );
    if (!expired.length) {
      Swal.fire({
        icon: "info",
        title: "Geen verlopen inschrijvingen",
        text: "Er zijn geen verlopen inschrijvingen om te archiveren.",
        confirmButtonColor: "#888",
      });
      return;
    }

    Swal.fire({
      icon: "warning",
      title: "Archiveer verlopen inschrijvingen",
      text: `Weet je zeker dat je ${expired.length} verlopen inschrijving(en) wilt archiveren?`,
      showCancelButton: true,
      confirmButtonColor: "#ec2027",
      confirmButtonText: "Archiveer",
      cancelButtonText: "Annuleren",
    }).then((res) => {
      if (!res.isConfirmed) return;
      setLoading(true);
      HttpClient.doRequest(
        Methods.POST,
        endpoints.renter.bulkArchiveExpiredRenters,
        {
          renterIds: expired.map((r) => r.id),
        }
      )
        .then(async (response) => {
          const message = await response.json().catch(() => null);
          if (response.ok) {
            const Toast = Swal.mixin({
              toast: true,
              position: "bottom-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
            Toast.fire({
              icon: message?.icon || "success",
              title: message?.title || "Verlopen inschrijvingen gearchiveerd",
            });
          } else {
            Swal.fire({
              icon: message?.icon || "error",
              title: message?.title || "Archiveren mislukt",
              text: message?.content || "Er is iets misgegaan.",
              confirmButtonColor: "#888",
            });
          }
        })
        .finally(() => refreshData());
    });
  };

  const onRowClick = (params) => {
    // Prevent navigation when in selection mode
    if (selectionCount > 0) return;
    navigate(appRoutes.shb.renter.detail + params.row.id);
  };

  return (
    <>
      {/* Ensure SweetAlert2 overlays appear above MUI Dialog (Dialog z-index ~1300) */}
      <GlobalStyles
        styles={{
          ".swal2-container": {
            zIndex: "1700 !important", // higher than MUI dialog & drawer layers
          },
        }}
      />
      <Dialog
        open={renewOpen}
        onClose={closeRenewDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {TranslationService?.l?.verlengInschrijving || "Verleng inschrijving"}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="nl">
            <DatePicker
              label={
                TranslationService?.l?.nieuweVervaldatum || "Nieuwe vervaldatum"
              }
              value={renewDate}
              format="DD/MM/YYYY"
              onChange={(m) => {
                if (m && m.isValid()) setRenewDate(m);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: {
                    "& .MuiInputBase-root": { backgroundColor: "transparent" },
                    "& .MuiFormLabel-root": { backgroundColor: "transparent" },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenewDialog}>
            {TranslationService?.l?.annuleren || "Annuleren"}
          </Button>
          <Button onClick={submitRenewal} variant="contained" color="primary">
            {TranslationService?.l?.opslaan || "Opslaan"}
          </Button>
        </DialogActions>
      </Dialog>
      <OverviewHeaderBar
        title="Inschrijvingen"
        actions={
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {selectionCount > 0 ? (
              // Keep header height stable while bulk bar shows above
              <Box sx={{ height: 40 }} />
            ) : (
              <>
                <TextField
                  size="small"
                  label="Zoeken"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button
                  variant="contained"
                  style={styles.newButton}
                  startIcon={<AddBoxIcon />}
                  onClick={() => setAddOpen(true)}
                >
                  Nieuw
                </Button>
                <CSVLink
                  data={data}
                  style={styles.exportToCsvLink}
                  headers={csvHeaders}
                  filename="huurders.csv"
                >
                  <Button
                    variant="contained"
                    startIcon={<SystemUpdateAltIcon />}
                    style={styles.exportToCsvButton}
                  >
                    Download CSV
                  </Button>
                </CSVLink>
                <Button
                  variant="contained"
                  style={styles.refreshButton}
                  startIcon={<RefreshIcon />}
                  onClick={refreshData}
                >
                  Vernieuw
                </Button>
                <Button
                  variant="contained"
                  style={styles.archiveExpiredButton}
                  startIcon={<ArchiveIcon />}
                  onClick={archiveExpiredRenters}
                >
                  Archiveer verlopen
                </Button>
              </>
            )}
          </Box>
        }
      />
      <Box>
        <SelectableDataGrid
          rows={tableRows}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r.id}
          onRowClick={onRowClick}
          clearSelectionOnRowsChange={false}
          // Listen for selection changes so we can hide the normal header actions
          onRowSelectionModelChange={(model) => {
            setSelectionCount(model?.ids?.size || 0);
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: "paymentExpiresAt", sort: "desc" }],
            },
          }}
          dataGridSx={{ backgroundColor: "background.paper" }}
          bulkBarSx={{ mx: 3 }}
          renderBulkActions={({ selection, clearSelection }) => (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MailOutlineIcon />}
                onClick={async () => {
                  await sendBulkActivationEmails(selection);
                  clearSelection();
                }}
              >
                Verstuur activatiemail
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<ArchiveIcon />}
                onClick={async () => {
                  await archiveSelected(selection);
                  clearSelection();
                }}
              >
                Archiveer geselecteerd
              </Button>
            </>
          )}
        />
      </Box>
      <AddRenterDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
        isSubmitting={isLoading}
      />
    </>
  );
}
